import { ReactNode } from 'react';
import { QuizWithEventInfo as Quiz } from 'graphql/fragments/types/QuizWithEventInfo';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import SailingIcon from '@mui/icons-material/Sailing';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CrisisAlertOutlinedIcon from '@mui/icons-material/CrisisAlertOutlined';
import { List, ListItem, ListItemIcon, ListItemText, IconButton, Tooltip, Typography, styled } from '@mui/material';
import { NavUtils, NavPages } from 'utils';
import { useNavigate, useParams } from 'react-router-dom';
import { QuizTypes } from 'consts';
import { useUserContext } from 'contexts';

import '../../styles/quiz-list.css'

interface Props {
    quizList: Quiz[];
    onCustomQuizSelect?: (selectedQuiz: Quiz) => void;
}

const StyledListItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: theme.palette.listColor.main,
    color: theme.palette.listColor.contrastText
}));

const StyledList = styled(List)({
    paddingBottom: '65px'
});

export const QuizList = (props: Props) => {
    const navigate = useNavigate();
    const user = useUserContext();
    const { eventAlias } = useParams();

    const onQuizSelect = (event: React.MouseEvent, selectedQuiz: Quiz) => {
        event.preventDefault();
        if (props.onCustomQuizSelect) {
            props.onCustomQuizSelect(selectedQuiz);
            return;
        }
        navigate(NavUtils.generateNavPath(NavPages.Quiz, selectedQuiz.id, selectedQuiz.event?.alias));
    };

    const onQuizEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, selectedQuiz: Quiz) => {
        event.preventDefault();
        navigate(NavUtils.generateNavPath(NavPages.EditQuiz, selectedQuiz.id, selectedQuiz.event?.alias));
    };

    const getQuizTypeIcon = (quizTypeId: number | null): ReactNode => {
        const quizType: QuizTypes = quizTypeId ?? QuizTypes.Slideshow;

        switch (quizType) {
            case QuizTypes.Battleship:
                return (
                    <Tooltip title={QuizTypes[quizType]}>
                        <SailingIcon />
                    </Tooltip>
                );
            case QuizTypes.Jeopardy:
                return (
                    <Tooltip title={QuizTypes[quizType]}>
                        <ViewWeekIcon />
                    </Tooltip>
                );
            case QuizTypes.Sharpshooter:
                return (
                    <Tooltip title={QuizTypes[quizType]}>
                        <CrisisAlertOutlinedIcon />
                    </Tooltip>
                );
            case QuizTypes.Slideshow:
            default:
                return (
                    <Tooltip title={QuizTypes[quizType]}>
                        
                        <SlideshowIcon />
                    </Tooltip>
                );
        }
    };

    return (
        <>
            <Typography variant="h4">Quiz List</Typography>
            <StyledList className="quiz-list">
                {props.quizList.map((quiz) => (
                    <StyledListItem className="quiz-list-item" key={`quiz-iteam-${quiz.id}`}>
                        <ListItemIcon onClick={(e) => onQuizSelect(e, quiz)}>{getQuizTypeIcon(quiz.quizTypeId)}</ListItemIcon>
                        <ListItemText
                            className="quiz-list-item-text"
                            onClick={(e) => onQuizSelect(e, quiz)}
                            id={`quiz-${quiz.name.replace(' ', '-')}`}
                            primary={quiz.name}
                        />
                        {user.isQuizCreatorOrEventAdmin(quiz.id, eventAlias) && (
                            <Tooltip title="Edit">
                                <IconButton onClick={(e) => onQuizEditClick(e, quiz)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Play">
                            <IconButton onClick={(e) => onQuizSelect(e, quiz)}>
                                <PlayCircleOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    </StyledListItem>
                ))}
            </StyledList>
        </>
    );
};
